/**
 * ConfirmDialog (RACCPE-20, shared deals primitives)
 *
 * A controlled confirm/decline dialog built on the existing Radix `Dialog`
 * primitive (which already provides focus-trapping, `aria-modal`, Esc-to-close
 * and scroll-locking — so no manual focus trap is needed). Replaces the bare
 * `window.confirm()` calls in the deal flows (CHP-223/233/225).
 *
 * Two shapes, one component:
 *   - plain confirm (e.g. "Approve registration") — no reason field;
 *   - reason-gated decline (e.g. "Decline registration") — pass `reason` with
 *     `required: true` and the confirm button stays disabled until the
 *     reviewer types a justification, which is handed back via onConfirm.
 */

import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export interface ConfirmDialogReasonConfig {
  label: string;
  placeholder?: string;
  /** When true, confirm is blocked until the field is non-empty. Default true. */
  required?: boolean;
  maxLength?: number;
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Destructive intent — red confirm button. */
  variant?: "default" | "destructive";
  /** Render a reason textarea; its trimmed value is passed to onConfirm. */
  reason?: ConfirmDialogReasonConfig;
  /** Disables both buttons + shows a working label while a mutation runs. */
  pending?: boolean;
  /** Extra disable gate for the confirm button, ANDed with the built-in
   * pending/reason-missing gates. Use when the dialog echoes editable mandatory
   * inputs (e.g. the closed-won value/date/PO) that must stay valid before the
   * request fires (CHP-809). */
  confirmDisabled?: boolean;
  /** Called with the trimmed reason (or undefined when no reason field). */
  onConfirm: (reason?: string) => void;
  /** Extra dialog content (e.g. ops-only fields) rendered after the reason
   * block, before the footer. State for these fields lives with the caller. */
  children?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  variant = "default",
  reason,
  pending = false,
  confirmDisabled: confirmDisabledProp = false,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const [reasonText, setReasonText] = React.useState("");

  // Reset the field each time the dialog opens so a prior attempt's text
  // doesn't leak into the next one.
  React.useEffect(() => {
    if (open) setReasonText("");
  }, [open]);

  const reasonRequired = reason ? (reason.required ?? true) : false;
  const reasonMissing = reasonRequired && reasonText.trim().length === 0;
  const confirmDisabled = pending || reasonMissing || confirmDisabledProp;

  function handleConfirm() {
    if (confirmDisabled) return;
    onConfirm(reason ? reasonText.trim() : undefined);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* When there's no description, explicitly opt out of Radix's
          aria-describedby auto-wiring (otherwise it warns about a dangling id).
          When there IS one, let Radix wire it to the DialogDescription. */}
      <DialogContent
        className="sm:max-w-md"
        {...(description ? {} : { "aria-describedby": undefined })}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {reason && (
          <div className="space-y-1">
            <Label htmlFor="confirm-dialog-reason">{reason.label}</Label>
            <Textarea
              id="confirm-dialog-reason"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              maxLength={reason.maxLength}
              placeholder={reason.placeholder}
              rows={3}
            />
          </div>
        )}

        {children}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={confirmDisabled}
            loading={pending}
            loadingLabel="Updating…"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
